// stores/pages.js - 스캔된 페이지 상태 관리
import { writable, derived, get } from 'svelte/store';

/** 최대 페이지 수 */
export const MAX_PAGES = 20;

/**
 * @typedef {Object} ScannedPage
 * @property {string} id - 고유 ID
 * @property {string} thumbnail - 썸네일 이미지 (필터 적용됨)
 * @property {string} fullImage - 원근 보정된 이미지
 * @property {string} rawImage - 원본 카메라 촬영 이미지 (코너 재조정용)
 * @property {'bw' | 'gray' | 'color' | null} filter - 적용된 필터
 * @property {Object | null} corners - 코너 좌표 (재편집용)
 */

/** 스캔된 페이지 배열 */
export const scannedPages = writable([]);

/** 페이지 수 (derived) */
export const pageCount = derived(scannedPages, $pages => $pages.length);

/** 페이지가 있는지 (derived) */
export const hasPages = derived(scannedPages, $pages => $pages.length > 0);

/** 최대 페이지 도달 여부 (derived) */
export const isMaxPages = derived(scannedPages, $pages => $pages.length >= MAX_PAGES);

/**
 * 페이지 추가
 * @param {Object} page - 페이지 데이터
 * @returns {boolean} 추가 성공 여부
 */
export function addPage(page) {
  const pages = get(scannedPages);
  if (pages.length >= MAX_PAGES) {
    return false;
  }

  scannedPages.update(p => [
    ...p,
    {
      id: `page_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      thumbnail: page.thumbnail || page.fullImage,
      fullImage: page.fullImage,
      rawImage: page.rawImage || page.fullImage,
      filter: page.filter || null,
      corners: page.corners || null,
    }
  ]);
  return true;
}

/**
 * 페이지 삭제
 * @param {string} id - 페이지 ID
 */
export function removePage(id) {
  scannedPages.update(p => p.filter(page => page.id !== id));
}

/**
 * 페이지 업데이트
 * @param {string} id - 페이지 ID
 * @param {Partial<ScannedPage>} updates - 업데이트할 필드
 */
export function updatePage(id, updates) {
  scannedPages.update(p =>
    p.map(page => page.id === id ? { ...page, ...updates } : page)
  );
}

/**
 * 페이지 재정렬
 * @param {number} fromIndex
 * @param {number} toIndex
 */
export function reorderPages(fromIndex, toIndex) {
  scannedPages.update(p => {
    const pages = [...p];
    const [removed] = pages.splice(fromIndex, 1);
    pages.splice(toIndex, 0, removed);
    return pages;
  });
}

/**
 * 모든 페이지 삭제
 */
export function clearPages() {
  scannedPages.set([]);
}

/**
 * 페이지 가져오기 (store 외부용)
 * @returns {ScannedPage[]}
 */
export function getPages() {
  return get(scannedPages);
}

/**
 * 특정 페이지 가져오기
 * @param {string} id
 * @returns {ScannedPage | undefined}
 */
export function getPage(id) {
  return get(scannedPages).find(p => p.id === id);
}

// --- 임시 캡처 저장 (세션 복원용) ---

const PENDING_CAPTURE_KEY = 'scantodesk_pending_capture';

/**
 * 촬영 사진 임시 저장
 * @param {string} imageData
 */
export function savePendingCapture(imageData) {
  try {
    localStorage.setItem(PENDING_CAPTURE_KEY, JSON.stringify({
      image: imageData,
      timestamp: Date.now(),
    }));
  } catch (e) {
    console.warn('Failed to save pending capture:', e);
  }
}

/**
 * 임시 저장된 촬영 사진 가져오기
 * @returns {{ image: string, timestamp: number } | null}
 */
export function getPendingCapture() {
  try {
    const stored = localStorage.getItem(PENDING_CAPTURE_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored);
    // 30분 이내만 유효
    if (Date.now() - data.timestamp > 30 * 60 * 1000) {
      clearPendingCapture();
      return null;
    }
    return data;
  } catch (e) {
    return null;
  }
}

/**
 * 임시 저장된 촬영 사진 삭제
 */
export function clearPendingCapture() {
  localStorage.removeItem(PENDING_CAPTURE_KEY);
}
